import React, { useState } from 'react'
import { KeyRound, Loader2, ShieldCheck, UserPlus } from 'lucide-react'
import { useChangePassword, useLogin, useSetup } from '../hooks/useAuth'

// The one screen the app shows before it will show anything else. It covers the
// three states the server can put an unauthenticated (or half-authenticated)
// visitor in, because they are the same form with different labels:
//
//   setup   — no accounts exist yet; the first one created becomes the admin
//   login   — the ordinary case
//   change  — signed in, but the server refuses everything until a new password
//             is set (an admin-issued password is a transport credential, not
//             the user's own)

const MIN_PASSWORD = 12

function Field({ label, hint, ...props }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-ink-soft dark:text-mist/60 mb-1">{label}</span>
      <input
        {...props}
        className="w-full rounded-lg border border-parchment-border bg-white/80 px-3 py-2 text-sm text-ink outline-none focus:border-gold focus:ring-2 focus:ring-gold/30 dark:border-midnight-600 dark:bg-midnight-900/60 dark:text-mist"
      />
      {hint && <span className="block mt-1 text-[11px] text-ink-soft/70 dark:text-mist/40">{hint}</span>}
    </label>
  )
}

export default function LoginPage({ mode = 'login' }) {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [current, setCurrent] = useState('')
  const [localError, setLocalError] = useState(null)

  const loginM = useLogin()
  const setupM = useSetup()
  const changeM = useChangePassword()
  const active = mode === 'setup' ? setupM : mode === 'change' ? changeM : loginM
  const busy = active.isPending

  const copy = {
    setup: {
      icon: UserPlus,
      title: 'Set up Veralis',
      blurb: 'No accounts exist yet. The first account you create is the administrator.',
      cta: 'Create administrator',
    },
    login: {
      icon: KeyRound,
      title: 'Sign in to Veralis',
      blurb: null,
      cta: 'Sign in',
    },
    change: {
      icon: ShieldCheck,
      title: 'Choose a new password',
      blurb: 'This account was given a temporary password. Set your own to continue.',
      cta: 'Set password',
    },
  }[mode]
  const Icon = copy.icon

  // Catch the two mistakes locally that the server would otherwise bounce a
  // round-trip later — everything else is the server's call to make.
  function validate() {
    if (mode !== 'login') {
      if (password.length < MIN_PASSWORD) return `Password must be at least ${MIN_PASSWORD} characters.`
      if (password !== confirm) return 'The two passwords do not match.'
    }
    return null
  }

  async function onSubmit(e) {
    e.preventDefault()
    const problem = validate()
    setLocalError(problem)
    if (problem) return
    try {
      if (mode === 'setup') await setupM.mutateAsync({ username, password, display_name: username })
      else if (mode === 'change') await changeM.mutateAsync({ current_password: current, new_password: password })
      else await loginM.mutateAsync({ username, password })
    } catch {
      // surfaced from the mutation's error below
    }
  }

  const err = localError || active.error?.message

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-parchment-100 dark:bg-midnight-900">
      <div className="w-full max-w-sm">
        <div className="mb-6 text-center">
          <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl border border-gold/40 bg-white/70 dark:border-gold/30 dark:bg-midnight-800">
            <Icon className="w-5 h-5 text-gold-deep dark:text-gold" />
          </div>
          <h1 className="mt-3 font-display text-2xl text-ink dark:text-mist">{copy.title}</h1>
          {copy.blurb && <p className="mt-1 text-sm text-ink-soft dark:text-mist/60">{copy.blurb}</p>}
        </div>

        <form
          onSubmit={onSubmit}
          className="rounded-xl border border-parchment-border bg-white/70 p-6 space-y-4 dark:border-midnight-600 dark:bg-midnight-800/60"
        >
          {mode !== 'change' && (
            <Field
              label="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              autoFocus
              required
            />
          )}
          {mode === 'change' && (
            <Field
              label="Current password"
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              autoFocus
              required
            />
          )}
          <Field
            label={mode === 'login' ? 'Password' : 'New password'}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
            hint={mode !== 'login' ? `At least ${MIN_PASSWORD} characters. Length beats punctuation.` : null}
            required
          />
          {mode !== 'login' && (
            <Field
              label="Repeat new password"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              required
            />
          )}

          {err && (
            <div
              role="alert"
              className="rounded-lg border border-rose-400/40 bg-rose-500/10 px-3 py-2 text-sm text-rose-700 dark:text-rose-300"
            >
              {err}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-gold px-4 py-2.5 text-sm font-medium text-midnight-900 hover:bg-gold-deep hover:text-white disabled:opacity-60"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {copy.cta}
          </button>
        </form>

        <p className="mt-4 text-center text-[11px] text-ink-soft/70 dark:text-mist/40">
          {mode === 'login'
            ? 'No self-service registration — ask an administrator for an account.'
            : 'Veralis'}
        </p>
      </div>
    </div>
  )
}
